import { forwardRef, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Icon } from './icon'
import { Input, type InputProps } from './input'

/**
 * PasswordInput — поле пароля с кнопкой «Показать / Скрыть пароль» (ТЗ заказчицы 26.10.2026,
 * п.9.2 «предпросмотр пароля при создании и входе»). Применяется ко ВСЕМ полям ввода пароля.
 *
 * Пароль скрыт по умолчанию; кнопка-«глаз» переключает видимость введённого значения и
 * сообщает состояние через `aria-pressed`. Кнопка — `type="button"`, поэтому форму не
 * отправляет. Собственный «глаз» Edge (`::-ms-reveal`) скрыт, чтобы не было двух кнопок.
 */
export type PasswordInputProps = Omit<InputProps, 'type'>

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, disabled, ...props }, ref) => {
    const [visible, setVisible] = useState(false)
    return (
      <div className="relative">
        <Input
          ref={ref}
          type={visible ? 'text' : 'password'}
          disabled={disabled}
          className={cn('pr-10 [&::-ms-reveal]:hidden', className)}
          {...props}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          disabled={disabled}
          aria-label={visible ? 'Скрыть пароль' : 'Показать пароль'}
          aria-pressed={visible}
          className={cn(
            'absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-8',
            'text-txt-tertiary outline-none transition-colors hover:text-txt',
            'focus-visible:ring-2 focus-visible:ring-accent',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          <Icon as={visible ? EyeOff : Eye} size="sm" />
        </button>
      </div>
    )
  },
)
PasswordInput.displayName = 'PasswordInput'
